import {
    Counts,
    RolloutCounts,
    RolloutMinute,
    SignalKind,
} from '../../api/rollout';

/** How wide one column of the timeline is. */
export const BUCKET_MS = 10 * 60 * 1000;

export interface Bucket {
    start: number;
    counts: Counts;
}

/** The labels of the kinds, as the page names them. */
export const KIND_LABEL: Record<SignalKind, string> = {
    Arrived: 'Aangekomen',
    Succeeded: 'Geslaagd',
    Failed: 'Mislukte pogingen',
    Postponed: 'Wacht',
    RateLimited: 'Exact-limiet',
    TokenRefreshed: 'Token vernieuwd',
    TokenFailed: 'Token mislukt',
};

/** The minutes of a timeline folded into columns from `from` to `to`, empty columns included. */
export const bucketize = (
    minutes: RolloutMinute[],
    from: number,
    to: number,
    size = BUCKET_MS
): Bucket[] => {
    const first = Math.floor(from / size) * size;
    const buckets: Bucket[] = [];

    for (let start = first; start < to; start += size) {
        buckets.push({ start, counts: {} });
    }

    for (const minute of minutes) {
        const at = Date.parse(minute.minute);
        const index = Math.floor((at - first) / size);

        if (index < 0 || index >= buckets.length) continue;

        const counts = buckets[index].counts;

        for (const [kind, value] of Object.entries(minute.counts) as [
            SignalKind,
            number
        ][]) {
            counts[kind] = (counts[kind] ?? 0) + value;
        }
    }

    return buckets;
};

export const countOf = (counts: Counts | undefined, kind: SignalKind) =>
    counts?.[kind] ?? 0;

const NO_OBJECTS: RolloutCounts = {
    offered: 0,
    arrived: 0,
    reoffered: 0,
    succeeded: 0,
    failed: 0,
    open: 0,
    failedAttempts: 0,
};

export interface ObjectTile {
    label: string;
    value: number;
    sub: string;
}

/**
 * The totals per object, so they add up: Aangeboden = Geslaagd + Mislukt + open, and
 * Aangeboden = nieuw + opnieuw. Attempts only in the sub-label of Mislukt.
 */
export const objectTiles = (
    after?: RolloutCounts | null,
    before?: RolloutCounts | null
): ObjectTile[] => {
    const a = after ?? NO_OBJECTS;
    const b = before ?? NO_OBJECTS;

    return [
        {
            label: 'Aangeboden',
            value: a.offered,
            sub: `${a.arrived} nieuw · ${a.reoffered} opnieuw · 24 u vóór: ${b.offered}`,
        },
        {
            label: 'Geslaagd',
            value: a.succeeded,
            sub: `${a.open} open · 24 u vóór: ${b.succeeded}`,
        },
        {
            label: 'Mislukt',
            value: a.failed,
            sub: `${a.failedAttempts} pogingen · 24 u vóór: ${b.failed}`,
        },
    ];
};

/** "2u 14m", "35m", "40s": a span as the page shows it. */
export const span = (ms: number) => {
    const seconds = Math.max(0, Math.floor(ms / 1000));

    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);

    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);

    return minutes % 60 === 0 ? `${hours}u` : `${hours}u ${minutes % 60}m`;
};

/** Where a watch stands: "nog 21 u 4 m" while it runs, "klaar" after. */
export const remaining = (endsAt: string, now: number) => {
    const left = Date.parse(endsAt) - now;

    return left > 0 ? `nog ${span(left)}` : 'klaar';
};

/** The commits of a build signature, "app=commit;..." as "exsnd 1a2b3c4d". */
export const builds = (signature?: string | null) =>
    (signature ?? '')
        .split(';')
        .filter(Boolean)
        .map((part) => {
            const [app, commit] = part.split('=');

            return `${app} ${(commit ?? '').slice(0, 8)}`;
        });

/** The id of an example object, "InvoiceSalesEntry|inv_1" as its type and id. */
export const example = (key: string) => {
    const bar = key.indexOf('|');

    return bar < 0
        ? { type: '', id: key }
        : { type: key.slice(0, bar), id: key.slice(bar + 1) };
};

/**
 * The commit the apps run, as one short label: "307dcf12" when they agree, "gemengd" while a
 * deploy is half way, "–" when nothing was compared yet.
 */
export const buildLabel = (signature?: string | null) =>
    commonBuild(parseBuilds(signature)) ?? '–';

/** What one app runs: a build number or a commit. */
export interface AppBuild {
    app: string;
    build?: string | null;
}

const APP_ROLE = /(cbrcv|cbsnd|exrcv|exsnd|extoken)$/i;

/** "c2econnectortestcbrcv" as "cbrcv", the name the Apps table uses. */
export const shortApp = (app: string) =>
    APP_ROLE.exec(app)?.[1].toLowerCase() ??
    (app.replace(/^.*connector(test|prod\w*)?/i, '') || app);

/** A signature "app=build;..." as one entry per app; a commit is cut to eight characters. */
export const parseBuilds = (signature?: string | null): AppBuild[] =>
    (signature ?? '')
        .split(';')
        .filter(Boolean)
        .map((part) => {
            const [app, build = ''] = part.split('=');

            return {
                app,
                build: /^[0-9a-f]{9,}$/i.test(build)
                    ? build.slice(0, 8)
                    : build,
            };
        });

/** The one build all apps run, "gemengd" when they differ, undefined when none is known. */
export const commonBuild = (apps: AppBuild[]) => {
    const distinct = Array.from(
        new Set(apps.map((a) => a.build).filter((b): b is string => !!b))
    );

    return distinct.length === 0
        ? undefined
        : distinct.length === 1
        ? distinct[0]
        : 'gemengd';
};

/** What runs now, as the header shows it: "20261030.2 · 2cd64bfd", "gemengd" or "–". */
export const runningLabel = (
    numbers?: string | null,
    commits?: string | null
) => {
    const parts = [
        commonBuild(parseBuilds(numbers)),
        commonBuild(parseBuilds(commits)),
    ].filter((p): p is string => !!p);

    if (parts.includes('gemengd')) return 'gemengd';

    return parts.length === 0 ? '–' : Array.from(new Set(parts)).join(' · ');
};

/**
 * What the apps run in one short line: "alle 5 apps op 20261030.2" when they agree; otherwise the
 * build most apps run and the apps that run another, "4 apps op 20261030.2 · cbrcv 20261029.3".
 */
export const buildSummary = (apps: AppBuild[]) => {
    const known = apps.filter(
        (a): a is AppBuild & { build: string } => !!a.build
    );

    if (known.length === 0) return '';

    const counts = new Map<string, number>();

    known.forEach((a) => counts.set(a.build, (counts.get(a.build) ?? 0) + 1));

    const [main, mainCount] = Array.from(counts.entries()).sort(
        (a, b) => b[1] - a[1] || b[0].localeCompare(a[0])
    )[0];
    const appCount = (n: number) => `${n} ${n === 1 ? 'app' : 'apps'}`;

    if (counts.size === 1) {
        return known.length === 1
            ? `${shortApp(known[0].app)} op ${main}`
            : `${known.length === apps.length ? 'alle ' : ''}${appCount(
                  known.length
              )} op ${main}`;
    }

    return [
        `${appCount(mainCount)} op ${main}`,
        ...known
            .filter((a) => a.build !== main)
            .map((a) => `${shortApp(a.app)} ${a.build}`),
    ].join(' · ');
};

/** The ranges the timeline can show, each with a column width that keeps it readable. */
export const RANGES = [
    {
        key: 'hour',
        label: 'Laatste uur',
        span: 3600 * 1000,
        bucket: 60 * 1000,
        per: 'per minuut',
    },
    {
        key: 'six',
        label: '6 uur',
        span: 6 * 3600 * 1000,
        bucket: 5 * 60 * 1000,
        per: 'per 5 minuten',
    },
    {
        key: 'window',
        label: 'Hele wacht',
        span: 0,
        bucket: BUCKET_MS,
        per: 'per 10 minuten',
    },
] as const;

export type RangeKey = (typeof RANGES)[number]['key'];

/** "23:41:07" in Dutch time. */
export const clockSeconds = (iso: string | number) =>
    new Date(iso).toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });

/** "29-10 16:35" in Dutch time. */
export const moment = (iso?: string | null) =>
    iso
        ? new Date(iso).toLocaleString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '–';

/** "2 u 14 m geleden", or "–". */
export const ago = (iso: string | null | undefined, now: number) =>
    iso ? `${span(now - Date.parse(iso))} geleden` : '–';

export const WATCH_STATUS: Record<string, string> = {
    watching: 'Bewaakt',
    done: 'Klaar',
    stopped: 'Gestopt',
};

/**
 * The watch an environment page shows. Without a watch in the URL it follows the newest, so a new
 * deploy or a new watch comes in by itself. A watch in the URL stays, unless it is the newest
 * anyway: then it is dropped from the URL (unpin) and the page follows from there on.
 */
export const shownWatch = (pinned: string | null | undefined, latest: string | undefined) => ({
    watchId: pinned || latest,
    older: !!pinned && !!latest && pinned !== latest,
    unpin: !!pinned && pinned === latest,
});

/** Owner as the page names it. */
export const OWNER_LABEL: Record<string, string> = {
    Automatic: 'Lost zichzelf op',
    Customer: 'Klant',
    Support: 'Wij',
    None: '–',
};
